<template>
  <app-layout class="Promo">
    <div :class="[
      'pt-gr-double-7 sm:pt-gr-double-8 lg:pt-gr-10 bg-gradient-to-br from-primary-900 to-tertiary-700 relative',
      { 'pb-gr-7 lg:pb-gr-8': !showPromoNav },
    ]">
      <img :src="blurredCover" alt="" class="object-cover z-1 absolute inset-0 opacity-30 w-full h-full" />
      <div
        class="container max-w-4xl relative z-10 flex flex-col sm:flex-row items-center sm:items-start sm:justify-start">
        <div class="mx-auto sm:mr-gr-6 md:mr-gr-7 min-h-gr-11 lg:min-h-gr-double-10 min-w-gr-11 w-gr-11 lg:w-gr-double-10 relative">
          <bundle-cover v-if="book.is_bundle" :book="book" class="shadow-lg rounded-lg !static" />
          <img v-else-if="book.cover" :src="`${book.cover}?w=1800&q=1&auto=format`" class="shadow-lg rounded-lg" alt="" />
          <sample-tracks hidden :tracks="promo.sample_tracks" :book="book" class="mt-gr-4 text-white" />
          <promo-price :promo="promo" class="absolute -left-gr-4 bottom-gr-7 text-base" />
        </div>
        <div class="max-w-sm md:max-w-md text-center sm:text-left sm:mr-auto">
          <book-meta :book="book" dark />

          <div v-if="user && user.email_verified_at == null">
            <div class="alert alert-warning bg-white mt-gr-5">
              <p>
                <i class="fa fa-envelope mr-1"></i>
                <b>
                  Please verify your email to retain access to your library!
                  Check your email for the link.
                </b>
              </p>
              <form class="mt-gr-half-6" :action="route('account.email.verify')" method="POST">
                <input type="hidden" name="_token" :value="$page.props.csrf_token" />
                <p class="text-sm italic">
                  Can't find it? <button type="submit" class="italic">Click here to send a new email verification
                    link</button>
                </p>
              </form>
            </div>
          </div>

          <div class="mt-gr-6 space-y-gr-5 sm:space-y-0 sm:space-x-gr-4 sm:flex items-center">
            <button v-if="showPlayBtn" @click="playNow"
              class="button w-full sm:w-auto bg-secondary-200 text-primary-900 rounded-full hover:bg-white hover:text-primary-700 focus:bg-white focus:text-primary-700">
              <span :class="{ hidden: isPlaying }">
                <i class="fa fa-xs fa-play mr-gr-4"></i>Play {{ canPlaySampleTracks || canPlayFreeTracks ? 'Sample' : '' }}
              </span>
              <span :class="{ hidden: !isPlaying }">
                <i class="fa fa-xs fa-pause mr-gr-4"></i>Pause
                {{ canPlaySampleTracks || canPlayFreeTracks ? 'Sample' : '' }}
              </span>
            </button>

            <subdomain-link v-if="!showAccessBtn && showBuyBtn" :href="buyLink"
              class="button w-full sm:w-auto text-lg rounded-full bg-white text-primary-700 hover:bg-secondary-100">
              <i class="fad fa-headphones-alt fa-lg mr-gr-4"></i>Buy Now
            </subdomain-link>
          </div>
          <book-description :book="book" dark />
        </div>
      </div>
      <expiry-message v-if="!promo.permissions?.canListenForever" :promo="promo"
        class="lg:-mb-gr-7 leading-gr-6 mt-gr-7 lg:mt-gr-8 relative z-10 container text-sm text-center text-white"
        expired-classes="text-secondary-200 font-bold" />
      <div v-if="showPromoNav"
        class="relative z-10 mt-gr-7 lg:mt-gr-8 py-gr-half-7 leading-gr-7 bg-black/10 dark:bg-primary-darkest/10">
        <div class="container text-center space-y-gr-4 sm:space-y-0 sm:flex flex-wrap justify-center"
          :class="isDownloading ? 'items-start' : 'items-center'">
          <subdomain-link v-if="showAccessBtn"
            class="button w-full sm:w-auto text-lg rounded-full bg-white text-primary-700 hover:bg-secondary-100"
            :direct="showBuyBtn" :href="showBuyBtn ? buyLink : route('listen.promo.access', { team, promo, next: 'listen' })
              ">
            <i class="fad fa-headphones-alt fa-lg mr-gr-4"></i>Get Access
          </subdomain-link>

          <subdomain-link v-if="showLoginBtn" class="button w-full sm:w-auto text-lg text-white hover:text-primary-300"
            :href="route('listen.promo.access', { team, promo })">
            <i class="fad fa-headphones-alt fa-lg mr-gr-4"></i>Login to Access
          </subdomain-link>

          <div v-if="showDownloadBtn" class="flex flex-col items-center justify-center">
            <a v-if="promo.permissions?.canDownload" :href="promo?.download || '#download'" @click="downloadTracks"
              target="_blank" rel="noopener" :download="promo?.download"
              :disabled="!playableTracks.length || isDownloading"
              class="button w-full sm:w-auto text-lg text-white hover:text-primary-300"
              :class="{ 'opacity-50': !playableTracks.length || isDownloading }">
              <span :hidden="isDownloading" class="mr-gr-4"><i class="fad fa-download fa-lg"></i></span>
              <span :hidden="!isDownloading" class="mr-gr-4"><i class="fad fa-spinner-third fa-spin fa-lg"></i></span>
              {{ isDownloading ? 'Downloading' : 'Download' }}
            </a>
            <small v-if="showAttemptsNotice"
              class="mt-gr-3 leading-gr-6 text-red-500 bg-red-100 px-gr-4 font-semibold rounded">
              You have
              {{ attemptsLeft === 0 ? 'no' : attemptsLeft }} download attempts left!
              <a v-if="attemptsLeft === 0" href="mailto:support@oneaudiobooks.com" class="link mx-gr-3">
                Email Support
              </a>
            </small>
            <small class="mt-gr-3 leading-gr-6 text-white" v-if="isDownloading">
              <span v-if="isZipping">
                {{ zipProgress }}... Preparing download file (~{{
                  humanReadableFilesize(downloadFileSize)
                }})
              </span>
              <span v-else-if="downloadCount">Please wait... Adding tracks {{ downloadCount }} of
                {{ playableTracks.length }}</span>
            </small>
          </div>

          <subdomain-link v-if="!showAccessBtn && showBuyBtn" :href="buyLink"
            class="button w-full sm:w-auto max-w-sm md:max-w-md text-lg rounded-full bg-white text-primary-700 hover:bg-secondary-100">
            <i class="fad fa-headphones-alt fa-lg mr-gr-4"></i>Buy Now
          </subdomain-link>

          <subdomain-link v-if="!showAccessBtn && showBuyBtn && !isAddedToCart" :href="addToCartLink"
            class="button w-full sm:w-auto max-w-sm md:max-w-md text-lg text-white hover:text-primary-300">
            <i class="fad fa-shopping-bag fa-lg mr-gr-4"></i>Add to Bag
          </subdomain-link>

          <button v-if="!showAccessBtn && showBuyBtn && isAddedToCart" @click.prevent.stop="toggleCart"
            class="button w-full sm:w-auto max-w-sm md:max-w-md text-lg text-white hover:text-primary-300">
            <i class="fas fa-check mr-gr-4"></i>In Your Bag
          </button>

          <a v-if="showAmazonLink" :href="amazonLink.url" target="_blank" rel="noopener"
            class="button w-full sm:w-auto text-lg text-white hover:text-primary-300">
            <i class="fad fa-book-open mr-gr-4"></i>{{ amazonLink.name || 'Amazon' }}
          </a>
          <a v-if="showRHBLink" :href="rhbLink.url" target="_blank" rel="noopener"
            class="button w-full sm:w-auto text-lg text-white hover:text-primary-300">
            <i class="fad fa-book-open mr-gr-4"></i>{{ rhbLink.name || 'RHB' }}
          </a>
          <div v-if="showOtherLinks">
            <a class="button font-light w-full sm:w-auto text-lg text-white hover:text-primary-300"
              v-for="link in promo.links" target="_blank" rel="noopener" :href="link.url" :key="link.id">
              {{ link.name }}<i class="fad fa-fw fa-xs fa-external-link-alt ml-gr-3"></i>
            </a>
            <a class="button font-light w-full sm:w-auto text-lg text-white hover:text-primary-300"
              v-for="link in book.links" target="_blank" rel="noopener" :href="link.url" :key="link.id">
              {{ link.name }}<i class="fad fa-fw fa-xs fa-external-link-alt ml-gr-3"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
    
    <div class="container max-w-4xl mt-gr-7">
      <bundle-promos v-if="book.is_bundle" :book="book" :promo="promo" :team="team" class="mt-gr-7" />
      <promo-tracks v-else :tracks="tracks" :book="book" :promo="promo" :team="team" :showAccessBtn="showAccessBtn"
        :showDownloadBtn="showDownloadBtn" :accessLink="showBuyBtn ? buyLink : route('listen.promo.access', { team, promo, next: 'listen' })
          " />
    </div>
    <promo-player :tracks="playableTracks" :book="book" class="fixed bottom-0 inset-x-0 z-40" />
    <teleport to="body">
      <div v-if="!showAccessBtn && isMobile && user && promo.permissions?.canListen && !hideAppLaunchDialog"
        class="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-md flex items-center justify-center"
        @click="hideAppLaunchDialog = true">
        <meta v-if="doRefresh" http-equiv="refresh" :content="refresh">
        <div
          class="relative bg-white w-full sm:w-grp-5 md:w-grp-4 lg:w-grp-3 md:rounded-xl shadow p-gr-6 flex flex-col items-center justify-center text-center space-y-gr-6"
          @click.stop="">
          <button class="absolute right-0 top-0 p-gr-5 opacity-50 hover:opacity-100"
            @click.stop="hideAppLaunchDialog = true">
            <i class="fa-light fa-sharp fa-fw fa-xmark-large fa-lg"></i>
          </button>
          <img v-if="book.cover" :src="`${book.cover}?w=1800&q=1&auto=format`" alt="" class="rounded"
            @click.prevent="doRefresh = true" />
          <i v-else class="!mt-0 fa-fw fa-sharp fa-light fa-headphones text-primary-700 text-5xl"></i>
          <p class="text-lg text-gray-700">
            <button class="link font-bold" @click.prevent="doRefresh = true">Ready to listen?</button>
            <br />
            <br />
            Open the <strong>ONE Library</strong> app to listen to <em>{{ book.title }}</em>.
          </p>
          <a :href="route('launch.app', ['listen', team.subdomain, promo.slug])" target="_blank"
            @click.prevent="doRefresh = true" class="button button-primary">
            Launch App
            <i class="fa-fw fa-sharp fa-light fa-external-link ml-gr-4 -mr-gr-3"></i>
          </a>
        </div>
      </div>
    </teleport>
  </app-layout>
</template>

<script>
import filesize from 'filesize';
import JSZip from 'jszip';
import { saveAs } from 'save-as';
import JSZipUtils from 'jszip-utils';
import axios from 'axios';
import AppLayout from '@/Layouts/AppLayout';
import SampleTracks from '@/Components/SampleTracks';
import PromoTracks from '@/Components/PromoTracks';
import PromoPlayer from '@/Components/PromoPlayer';
import PromoPrice from '@/Components/PromoPrice';
import ExpiryMessage from '@/Components/ExpiryMessage';
import { AudioBus, CartBus } from '@/Utils/bus';
import { isSafari, isMobile } from '@/Utils/device';
import { getCookie, setCookie } from '@/Utils/cookies';
import BundleCover from '@/Components/BundleCover';
import BundlePromos from '@/Components/BundlePromos';
import BookMeta from '@/Components/BookMeta';
import BookDescription from '@/Components/BookDescription';

export default {
  name: 'Promo',

  props: ['promo', 'tracks', 'user', 'team', 'book'],

  components: {
    AppLayout,
    SampleTracks,
    PromoTracks,
    ExpiryMessage,
    PromoPlayer,
    PromoPrice,
    BundleCover,
    BundlePromos,
    BookMeta,
    BookDescription,
  },

  data() {
    return {
      showDescription: false,
      isPlaying: false,
      isDownloading: false,
      isZipping: false,
      zipProgress: '0%',
      downloadCount: 1,
      downloadFileSize: 0,
      attemptsLeft: 5,
      isSafari,
      isMobile: isMobile(),
      doRefresh: false,
      hideAppLaunchDialog: true,
    };
  },

  computed: {
    refresh() {
      return `0; oneaudiobooks://listen/${this.team.subdomain}/${this.promo.slug}`;
    },

    isAddedToCart() {
      let cartItems = this.$page.props?.itemsForNow || [];
      return cartItems.some((cartItem) => cartItem.promo_id == this.promo.id);
    },

    blurredCover() {
      return `${this.book.cover}?w=500&blur=300&blend=111B2DFF&blend-mode=normal`;
    },

    amazonLink() {
      if (this.promo?.amazon_links?.length) return this.promo.amazon_links[0];
      if (this.book?.amazon_links?.length) return this.book.amazon_links[0];
      return null;
    },

    rhbLink() {
      if (this.promo?.rhb_links?.length) return this.promo.rhb_links[0];
      if (this.book?.rhb_links?.length) return this.book.rhb_links[0];
      return null;
    },

    // downloadLink() {
    //   return this.user
    //     ? route('listen.promo.download', {team, promo})
    //     : route('listen.promo.access', { team, promo, next: 'download' });
    // },

    buyLink() {
      let team = this.team;
      let promo = this.promo;
      return this.user
        ? route('listen.promo.buy', { team, promo })
        : route('listen.promo.access', { team, promo, next: 'buy' });
    },

    addToCartLink() {
      let team = this.team;
      let promo = this.promo;
      return this.user
        ? route('listen.promo.addtocart', { team, promo })
        : route('listen.promo.access', { team, promo, next: 'addtocart' });
    },

    showPromoNav() {
      return (
        this.showAccessBtn ||
        this.showLoginBtn ||
        this.showDownloadBtn ||
        this.showBuyBtn ||
        this.showAmazonLink ||
        this.showOtherLinks
      );
    },

    showLoginBtn() {
      return !this.showAccessBtn && !this.promo.permissions?.canView;
    },

    showAccessBtn() {
      return (
        !this.showBuyBtn &&
        this.promo.permissions?.canSignup &&
        !this.promo.permissions?.canListen &&
        (this.promo.book?.is_bundle || this.playableTracks.length < this.promo.tracks?.length)
      );
    },

    showDownloadBtn() {
      return (
        !this.showAccessBtn &&
        this.canPlayTracks &&
        this.promo.permissions?.canDownload &&
        this.user
      );
    },

    showBuyBtn() {
      return this.promo.permissions?.canPurchase && !this.promo.permissions?.canListen;
    },

    showAmazonLink() {
      return !!this.amazonLink;
    },

    showRHBLink() {
      return !!this.rhbLink;
    },

    showOtherLinks() {
      return this.promo.links?.length > 0 || this.book.links?.length > 0;
    },

    showPlayBtn() {
      return this.canPlayTracks || this.canPlayFreeTracks || this.canPlaySampleTracks;
    },

    canPlayTracks() {
      return this.playableTracks?.length > 0;
    },

    playableTracks() {
      return this.promo?.tracks?.filter((track) => !!track.track);
    },

    canPlayFreeTracks() {
      return this.canPlayTracks && !this.promo.permissions?.canListen;
    },

    canPlaySampleTracks() {
      return !this.canPlayTracks && !this.canPlayFreeTracks && this.promo.sample_tracks?.length;
    },

    showAttemptsNotice() {
      return !this.isDownloading && this.attemptsLeft < 2;
    },
  },

  mounted() {
    AudioBus.$on('play', () => {
      this.isPlaying = true;
    });
    AudioBus.$on('pause', () => {
      this.isPlaying = false;
    });
  },

  beforeUnmount() {
    AudioBus.$off('play');
    AudioBus.$off('pause');
  },

  methods: {
    toggleCart() {
      CartBus.$emit('toggle');
    },

    playNow() {
      AudioBus.$emit('playNow', {
        playSample: this.canPlaySampleTracks,
        playTracks: this.playableTracks,
      });
    },

    downloadTracks(ev) {
      if (!this.promo.download) ev.preventDefault();

      // Add to download count and download attempts
      axios.post(route('download.update', { promo: this.promo })).then(({ data }) => {
        this.attemptsLeft = Math.max(data.attempts_left, 0);

        // Now download
        if (data?.message === 'success') {
          // Don't use link's href
          ev.preventDefault();

          // Download manually
          this.isDownloading = true;
          this.downloadCount = 1;
          this.downloadFileSize = 0;
          this.zipProgress = '0%';
          let zip = new JSZip();
          let zipFilename = `${this.promo.name}.zip`;

          this.playableTracks.forEach((track) => {
            let src = track.track;

            JSZipUtils.getBinaryContent(src, (err, data) => {
              if (err) {
                alert(
                  `An error occurred. Please, try again or download each track individually.`,
                );
                this.isDownloading = false;
                throw err;
              }

              this.downloadFileSize = this.downloadFileSize + data.byteLength;

              zip.file(track.filename, data, { binary: true });
              this.downloadCount = this.downloadCount + 1;

              if (this.downloadCount === this.playableTracks.length + 1) {
                this.isZipping = true;
                zip
                  .generateAsync({ type: 'blob' }, (metadata) => {
                    this.zipProgress = metadata.percent.toFixed(1) + '%';
                  })
                  .then((content) => {
                    saveAs(content, zipFilename);
                    this.isDownloading = false;
                    this.isZipping = false;
                  })
                  .catch(() => {
                    alert(
                      `An error occurred. Please, try again or download each track individually.`,
                    );
                    this.isDownloading = false;
                    this.isZipping = false;
                  });
              }
            });
          });
        } else {
          this.$nextTick(() => {
            setTimeout(() => {
              alert(
                data?.message || 'Sorry, download failed. Please, keep tab open and try again.',
              );
            }, 50);
          });
        }
      });
    },

    humanReadableFilesize(bytes) {
      return filesize(bytes);
    },
  },
};
</script>

<style lang="scss" src="./Promo.scss" scoped></style>
